import { ClearFilterButton, DateRangeFilter, TimesheetsToggler } from '@app/components/core';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { SprintReportTab } from '@app/utils/types';
import { ProductEntity } from '@data';
import cx from 'classnames';
import React, { Dispatch, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { filterBarClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';

export interface SprintHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  organizationId: string;
  /** Whether the viewer may open the team's reporting, which only PMs and admins may. */
  canSeeMembers: boolean;
  activeTab: SprintReportTab;
  setActiveTab: Dispatch<SprintReportTab>;
  productIds: string[];
  setProductIds: Dispatch<string[]>;
  products?: { items: ProductEntity[]; count: number };
  from: string;
  to: string;
  setFrom: Dispatch<string>;
  setTo: Dispatch<string>;
  fromMissing?: boolean;
  toMissing?: boolean;
}

const SprintHeader = ({
  organizationId,
  canSeeMembers,
  activeTab,
  setActiveTab,
  productIds,
  setProductIds,
  products,
  from,
  to,
  setFrom,
  setTo,
  fromMissing,
  toMissing,
}: SprintHeaderProps): ReactElement => {
  const { t } = useTranslation();

  const productsOptions = products?.items?.map(product => ({ value: product.id, label: product.name })) || [];

  return (
    <>
      <div
        className={cx(pageHeaderRowClass, 'mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between')}
      >
        <h1 className={pageTitleClass}>{t('titles.sprintReporting')}</h1>
        <div className="flex flex-wrap items-center gap-3 md:gap-6">
          <TimesheetsToggler
            active={activeTab}
            setTab={setActiveTab}
            organizationId={organizationId}
            canSeeMembers={canSeeMembers}
          />
        </div>
      </div>
      {/* Overview only: "To report" is the fixed rolling window of weekdays you owe, and that question has
          no product or date dimension, so a filter over it would narrow nothing. */}
      {activeTab === SprintReportTab.completed && (
        <div className={`mb-6 ${filterBarClass}`}>
          <div className="w-full max-w-[340px]">
            <MultiSelectField
              label={t('titles.products')}
              options={productsOptions}
              value={productIds}
              onChange={setProductIds}
              emptyLabel={t('allProducts')}
              searchPlaceholder={t('search')}
              emptySearchLabel={t('noMatches')}
              renderCountSummary={count => t('nItemsSelected', { count })}
            />
          </div>
          <DateRangeFilter
            from={from}
            to={to}
            onFromChange={setFrom}
            onToChange={setTo}
            fromMissing={fromMissing}
            toMissing={toMissing}
            hideClear
          />
          {(productIds.length > 0 || (from && to)) && (
            <ClearFilterButton
              onClick={() => {
                setProductIds([]);
                setFrom('');
                setTo('');
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
export default SprintHeader;
